import loginBgDefault from '@/assets/images/login-bg.png';
import logoWorks from '@/assets/images/logos/logo-works.png';
import logoYW from '@/assets/images/logos/yorkshire-water/logo.png';

export type ClientId = 'works-lite' | 'yorkshire-water';

export interface ClientTheme {
  primary: string;
  primaryHover: string;
  primaryForeground: string;
  sidebarActiveBg: string;
  sidebarActiveText: string;
}

export interface ClientFeatures {
  requestMoreInfo: boolean;
  bulkActions: boolean;
  callNow: boolean;
  helpPage: boolean;
  thirdPartyAccounts: boolean;
  durationChallenge: boolean;
  inactivityTimeoutMinutes: number | null;
  // Show the monetary dashboard charts (Total Value by Job Type, Total Job
  // Value Monthly Trend). Hidden for clients that don't surface £ value.
  dashboardValueCharts: boolean;
  // Show the 1–7 day range selector on the Schedule Map view (SC-6254). When
  // off, the map keeps a fixed default window with no user-facing control.
  // Defaults off — currently a Yorkshire Water-only control.
  mapViewRangeSelector: boolean;
  psrFilter: boolean;
  regions: boolean;
  coreValidationWaitingNotice: boolean;
  /** Show the application reference field on the Job Detail page (e.g. Connect Me ref for YW). */
  showApplicationReference: boolean;
  // IANA timezone the scheduler interprets typed job start/end wall-clock times
  // in (WRK-5697). A time entered in the Schedule modal means this zone's wall
  // clock regardless of the planner's own browser timezone, so the "start is in
  // the past" check and the instant POSTed to the backend agree with the
  // backend's own London-based guard. Every current brand is UK, so this
  // defaults to 'Europe/London'; a non-UK brand overrides it here.
  schedulingTimeZone: string;
}

export type MessageSender = 'skewbworks' | 'core' | 'connectme';

export interface MessageSourceConfig {
  show: boolean;
  label: string;
}

export type MessageSources = Record<MessageSender, MessageSourceConfig>;

export interface ClientAccessControlConfig {
  hiddenSubjects: readonly string[];
  subjectLabelOverrides: Readonly<Record<string, string>>;
}

/**
 * Cognito SSO (single sign-on) config for a client. When `enabled`, the login
 * page shows a "Log in with {name}" banner that starts the OAuth flow against
 * the backend `/auth/sso/*` endpoints.
 */
export interface ClientSsoConfig {
  enabled: boolean;
  // Optional Cognito identity-provider name; when set, Cognito redirects
  // straight to that federated IdP instead of showing its hosted-UI chooser.
  identityProvider?: string;
  // Optional label override for the SSO button; defaults to the client name.
  buttonLabel?: string;
}

/**
 * The fully-resolved config a brand runs with. Consumers read this shape
 * (`clientConfig.features.x`, `clientConfig.messageSources[sender]`).
 */
export interface ClientConfig {
  id: ClientId;
  name: string;
  logo: string;
  logoHeight: string;
  logoWidth: string;
  loginBackground: string;
  theme: ClientTheme;
  features: ClientFeatures;
  messageSources: MessageSources;
  accessControl: ClientAccessControlConfig;
  sso: ClientSsoConfig;
}

/**
 * A brand *declaration*: its identity/theme plus only the deltas it changes from
 * the code defaults below. Feature flags and message sources are declared as
 * partial overrides — anything a brand does not mention keeps its default. This
 * keeps each brand block small (a readable list of "what this client changes")
 * and means adding a new flag/source is a one-line default, not an edit to every
 * brand.
 */
interface ClientDefinition {
  id: ClientId;
  name: string;
  logo: string;
  logoHeight: string;
  logoWidth: string;
  loginBackground: string;
  theme: ClientTheme;
  featureOverrides?: Partial<ClientFeatures>;
  messageSourceOverrides?: Partial<Record<MessageSender, Partial<MessageSourceConfig>>>;
  accessControl: ClientAccessControlConfig;
  ssoOverrides?: Partial<ClientSsoConfig>;
}

// ---------------------------------------------------------------------------
// CODE DEFAULTS
// The baseline every brand inherits unless it overrides a value. works-lite /
// Cadent runs on these defaults with no overrides at all.
// ---------------------------------------------------------------------------

const DEFAULT_FEATURES: ClientFeatures = {
  requestMoreInfo: true,
  bulkActions: true,
  callNow: true,
  helpPage: true,
  thirdPartyAccounts: true,
  durationChallenge: true,
  inactivityTimeoutMinutes: null,
  dashboardValueCharts: true,
  // Off by default (WRK-5567 removed the map's day-range control); reinstated
  // only for brands that opt in via override.
  mapViewRangeSelector: false,
  psrFilter: true,
  regions: false,
  coreValidationWaitingNotice: true,
  showApplicationReference: false,
  // Every current brand operates in the UK; a non-UK brand overrides this.
  schedulingTimeZone: 'Europe/London',
};

const DEFAULT_MESSAGE_SOURCES: MessageSources = {
  skewbworks: { show: true, label: 'SkewbWorks' },
  core: { show: true, label: 'CORE Cockpit' },
  connectme: { show: false, label: 'ConnectMe (Customer)' },
};

// SSO is off unless a brand opts in; password login is the default path.
const DEFAULT_SSO: ClientSsoConfig = {
  enabled: false,
};

// ---------------------------------------------------------------------------
// MERGE
// Client config overrides code defaults; unspecified keys stay default.
// ---------------------------------------------------------------------------

function resolveFeatures(overrides?: Partial<ClientFeatures>): ClientFeatures {
  return { ...DEFAULT_FEATURES, ...overrides };
}

function resolveMessageSources(
  overrides?: Partial<Record<MessageSender, Partial<MessageSourceConfig>>>,
): MessageSources {
  const resolved = {} as MessageSources;
  for (const key of Object.keys(DEFAULT_MESSAGE_SOURCES) as MessageSender[]) {
    resolved[key] = { ...DEFAULT_MESSAGE_SOURCES[key], ...overrides?.[key] };
  }
  return resolved;
}

function buildClientConfig(def: ClientDefinition): ClientConfig {
  return {
    id: def.id,
    name: def.name,
    logo: def.logo,
    logoHeight: def.logoHeight,
    logoWidth: def.logoWidth,
    loginBackground: def.loginBackground,
    theme: def.theme,
    features: resolveFeatures(def.featureOverrides),
    messageSources: resolveMessageSources(def.messageSourceOverrides),
    accessControl: def.accessControl,
    sso: { ...DEFAULT_SSO, ...def.ssoOverrides },
  };
}

// ---------------------------------------------------------------------------
// BRAND DEFINITIONS (deltas only)
// ---------------------------------------------------------------------------

const worksLiteDefinition: ClientDefinition = {
  id: 'works-lite',
  name: 'Skewb Works Lite',
  logo: logoWorks,
  logoHeight: '32px',
  logoWidth: '120px',
  loginBackground: loginBgDefault,
  theme: {
    primary: '#E84A2B',
    primaryHover: '#d43d20',
    primaryForeground: '#ffffff',
    sidebarActiveBg: '#E84A2B',
    sidebarActiveText: '#ffffff',
  },
  // works-lite/Cadent runs on every default: no feature or message-source
  // overrides, full RBAC matrix, default labels. ConnectMe booking notes stay
  // hidden here (default connectme source is show: false).
  accessControl: {
    hiddenSubjects: [],
    subjectLabelOverrides: {},
  },
  // SSO off by default for the base works-lite brand (no ssoOverrides needed).
};

const yorkshireWaterDefinition: ClientDefinition = {
  id: 'yorkshire-water',
  name: 'Yorkshire Water',
  logo: logoYW,
  logoHeight: '40px',
  logoWidth: '104px',
  loginBackground: loginBgDefault,
  theme: {
    primary: '#003DA5',
    primaryHover: '#002d7a',
    primaryForeground: '#ffffff',
    sidebarActiveBg: '#003DA5',
    sidebarActiveText: '#ffffff',
  },
  featureOverrides: {
    requestMoreInfo: false,
    bulkActions: false,
    callNow: false,
    helpPage: false,
    thirdPartyAccounts: false,
    durationChallenge: false,
    inactivityTimeoutMinutes: 30,
    dashboardValueCharts: false,
    // SC-6254: YW gets the reinstated 1–7 day range selector on the Map view.
    mapViewRangeSelector: true,
    psrFilter: false,
    regions: true,
    coreValidationWaitingNotice: false,
    showApplicationReference: true,
  },
  messageSourceOverrides: {
    core: { label: 'System' },
    connectme: { show: true },
  },
  accessControl: {
    hiddenSubjects: [],
    subjectLabelOverrides: {},
  },
  // Yorkshire Water uses federated SSO — shows a "Log in with Yorkshire Water"
  // banner on the login page. `identityProvider` must match the Cognito
  // provider_name exactly (Cognito 400s on an unknown provider), and YW has two
  // OIDC providers — one per Azure app registration. This is the web app, so it
  // uses the Works provider; Skewb Mobile uses YorkshireWaterMobileAD.
  ssoOverrides: {
    enabled: true,
    identityProvider: 'YorkshireWaterWorksAD',
  },
};

/**
 * Every brand's fully-resolved config (defaults merged with its overrides),
 * keyed by brand. Exported so tests can assert the merge for a specific brand
 * without depending on the `VITE_APP_BRAND` build env.
 */
export const clientConfigsByBrand: Record<string, ClientConfig> = {
  'works-lite': buildClientConfig(worksLiteDefinition),
  'yorkshire-water': buildClientConfig(yorkshireWaterDefinition),
};

function resolveClientConfig(): ClientConfig {
  const brandKey = import.meta.env.VITE_APP_BRAND;
  if (brandKey && brandKey in clientConfigsByBrand) {
    return clientConfigsByBrand[brandKey];
  }
  return clientConfigsByBrand['works-lite'];
}

export const clientConfig = resolveClientConfig();
